import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, 
  Filter, 
  Eye,
  Package,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminOrdersPage = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [page, setPage] = useState(0);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '20');
      params.append('skip', String(page * 20));

      const response = await axios.get(`${API}/admin/orders?${params}`, { headers });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchOrders();
  };

  const getStatusColor = (status) => {
    const colors = {
      payment_pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      fulfilled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      partially_fulfilled: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      refunded: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const statuses = ['', 'payment_pending', 'paid', 'fulfilled', 'cancelled', 'refunded'];

  return (
    <div className="p-6 lg:p-8" data-testid="admin-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Orders</h1>
          <p className="text-[#888] text-sm">{orders.length} orders found</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchOrders}
          className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a]"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by order # or email..."
                className="pl-10 bg-[#111] border-[#3a3a3a] text-white placeholder:text-[#555]"
                data-testid="orders-search-input"
              />
            </div>
          </form>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#666]" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              className="bg-[#111] border border-[#3a3a3a] rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#D4540A]"
              data-testid="orders-status-filter"
            >
              <option value="">All Statuses</option>
              {statuses.slice(1).map((s) => (
                <option key={s} value={s}>{s.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-[#444] mx-auto mb-4" />
            <p className="text-[#888]">No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2a2a2a]">
                  <th className="text-left p-4 text-[#888] font-medium text-sm">Order #</th>
                  <th className="text-left p-4 text-[#888] font-medium text-sm">Date</th>
                  <th className="text-left p-4 text-[#888] font-medium text-sm">Customer</th>
                  <th className="text-left p-4 text-[#888] font-medium text-sm">Items</th>
                  <th className="text-left p-4 text-[#888] font-medium text-sm">Total</th>
                  <th className="text-left p-4 text-[#888] font-medium text-sm">Status</th>
                  <th className="text-left p-4 text-[#888] font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="border-b border-[#2a2a2a] hover:bg-[#222] transition-colors"
                    data-testid={`order-row-${order.order_id || order.id}`}
                  >
                    <td className="p-4">
                      <span className="font-mono text-white font-medium">
                        {order.order_id || order.id.slice(0, 8)}
                      </span>
                    </td>
                    <td className="p-4 text-[#888] text-sm">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <p className="text-white text-sm">
                        {order.customer_name || `${order.shipping?.first_name} ${order.shipping?.last_name}`}
                      </p>
                      <p className="text-[#666] text-xs">
                        {order.email || order.shipping?.email}
                      </p>
                    </td>
                    <td className="p-4 text-[#888] text-sm">
                      {order.items?.length || 0} items
                    </td>
                    <td className="p-4 text-white font-medium">
                      ${(order.totals?.total || order.total || 0).toFixed(2)}
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(order.status)}`}>
                        {order.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4">
                      <Link to={`/admin/orders/${order.order_id || order.id}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#888] hover:text-white hover:bg-[#2a2a2a]"
                          data-testid={`view-order-${order.order_id || order.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {orders.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-[#2a2a2a]">
            <p className="text-[#888] text-sm">
              Showing {page * 20 + 1} - {page * 20 + orders.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a] disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-white px-3">Page {page + 1}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={orders.length < 20}
                className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a] disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage;
